import React, { useMemo, useState } from "react";
import { Container, Row, Col, Button, Badge, Offcanvas } from "react-bootstrap";
import { motion } from "framer-motion";
import { 
  FaUserTie, FaCheckCircle, FaGavel, FaLightbulb, 
  FaUniversity, FaArrowRight, FaFileContract
} from "react-icons/fa";

// Components
import Header from "../components/Header";
import Footer from "../components/Footer";

const AdmissionPolicy = () => {
  const [showDrawer, setShowDrawer] = useState(false);

  // Memoize static data
  const committee = useMemo(() => [
    { name: "Prof. Dr. Perviaz A. Memon", title: "Chairman", pos: "Professor in Business Administration" },
    { name: "Mr. M. Ismail Mangrio", title: "Deputy Chairman", pos: "Assistant Professor in Computer Science" },
    { name: "Mr. Azhar Ali Soomro", title: "Member", pos: "Registrar" },
    { name: "Mr. Muhammad Ali Shaikh", title: "Member/Secretary", pos: "Deputy Manager (Admissions)" },
  ], []);

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Replicated Sub-Nav Styling */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <h1 className="h4 fw-900 text-navy m-0">Admission <span className="text-primary">Policy</span></h1>
              <small className="text-muted d-flex align-items-center gap-2">
                 <span className="sync-dot-mini"></span> Official Guidelines 2024-25
              </small>
            </Col>
            <Col md={6}>
              <div className="d-flex gap-3 justify-content-end align-items-center mt-3 mt-md-0">
                <Button 
                    variant="primary" 
                    className="rounded-pill px-4 d-lg-none d-flex align-items-center gap-2 nav-hint-pulse shadow-sm h-100"
                    onClick={() => setShowDrawer(true)}
                >
                    <FaGavel /> <span className="fw-bold small">Committee</span>
                </Button>
                <div className="d-none d-md-flex align-items-center gap-2 bg-light-soft px-3 py-2 rounded-pill">
                    <FaFileContract className="text-primary" />
                    <span className="small fw-bold text-navy">Academic Integrity</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Desktop Sidebar - Committee */}
            <Col lg={4} className="d-none d-lg-block">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">ADMISSION COMMITTEE</div>
                  <div className="program-list custom-scrollbar p-3">
                    {committee.map((member, idx) => (
                      <div key={idx} className="committee-member-item p-3 rounded-4 mb-3 border-0 bg-white shadow-sm transition-all hover-translate-y">
                        <div className="d-flex align-items-center gap-3">
                            <div className="icon-circle-mini bg-light-soft text-primary">
                                <FaUserTie />
                            </div>
                            <div className="text-start">
                                <div className="name fw-bold mb-0 text-navy small">{member.name}</div>
                                <Badge bg="soft-primary" className="mt-1 text-primary">{member.title}</Badge>
                                <div className="stats-tag mt-1" style={{ fontSize: '0.65rem' }}>{member.pos}</div>
                            </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 border-top bg-navy text-white text-center">
                    <FaUniversity size={30} className="mb-3 opacity-30" />
                    <h6 className="fw-bold mb-2">Ready to Apply?</h6>
                    <Button variant="warning" href="/apply" className="w-100 rounded-pill fw-bold py-2 mt-2">
                        Get Started <FaArrowRight className="ms-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </Col>

            {/* Main Content */}
            <Col lg={8}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm">
                    <div className="section-intro mb-5">
                      <div className="category-tag">Policy Statement</div>
                      <h2 className="portal-heading text-navy display-5 mb-3">Policy Overview</h2>
                      <div className="ivy-divider mb-4" />
                      <p className="editorial-text-body text-muted">
                        At Sukkur IBA University, we are committed to delivering world-class education while 
                        maintaining a strict merit-based admission process. Our aim is to produce 
                        graduates equipped with practical skills and high ethical values.
                      </p>
                    </div>

                    <Row className="mb-5 g-4">
                        <Col md={6}>
                            <div className="feature-block p-4 rounded-4 bg-light-soft h-100 border-start border-primary border-4">
                                <h5 className="fw-800 text-navy mb-3"><FaLightbulb className="text-primary me-2"/> Core Principles</h5>
                                <ul className="policy-list small">
                                    <li className="mb-2">Quality education at an affordable cost.</li>
                                    <li className="mb-2">Global institutional collaborations.</li>
                                    <li className="mb-2">Unique foundation semester model.</li>
                                    <li>Focus on critical research capabilities.</li>
                                </ul>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="feature-block p-4 rounded-4 bg-light-soft h-100 border-start border-primary border-4">
                                <h5 className="fw-800 text-navy mb-3"><FaCheckCircle className="text-primary me-2"/> Process & Merit</h5>
                                <ul className="policy-list small">
                                    <li className="mb-2">Admissions based strictly on merit.</li>
                                    <li className="mb-2">Campus-specific aptitude testing.</li>
                                    <li className="mb-2">3-year degree gap limit for UG.</li>
                                    <li>Two-year foundational study focus.</li>
                                </ul>
                            </div>
                        </Col>
                    </Row>

                    <div className="policy-conditions">
                        <h4 className="fw-800 text-navy mb-4 d-flex align-items-center">
                            <FaFileContract className="text-primary me-3" />
                            Conditions for Admission
                        </h4>
                        <div className="condition-strip-stack">
                             {[
                                "Submit online application & pay processing fee.",
                                "Qualify Aptitude Test & mandatory interviews.",
                                "Fulfill academic eligibility per program.",
                                "Equivalence certificate for foreign credentials."
                              ].map((text, i) => (
                                <div key={i} className="condition-strip d-flex align-items-center gap-3 p-3 bg-white border rounded-4 mb-3 transition-all hover-translate-y">
                                    <div className="step-num-circle">{i + 1}</div>
                                    <div className="fw-700 text-navy small">{text}</div>
                                </div>
                              ))}
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="mt-5 pt-4 border-top d-flex flex-column flex-md-row gap-3">
                         <Button variant="primary" href="/programs" className="rounded-pill px-5 py-3 fw-bold flex-grow-1 shadow-sm">
                            Explore All Programs
                         </Button>
                         <Button variant="outline-primary" href="/admission-procedure" className="rounded-pill px-5 py-3 fw-bold flex-grow-1">
                            How to Apply
                         </Button>
                    </div>
                </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- MOBILE COMMITTEE DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-75 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Admission Committee</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 bg-light-soft">
            <div className="program-list-mobile p-3">
                {committee.map((member, idx) => (
                    <div key={idx} className="committee-member-item p-3 rounded-4 mb-3 bg-white shadow-sm">
                        <div className="d-flex align-items-center gap-3">
                            <div className="icon-circle-mini bg-light-soft text-primary">
                                <FaUserTie />
                            </div>
                            <div className="text-start">
                                <div className="name fw-bold mb-0 text-navy small">{member.name}</div>
                                <Badge bg="soft-primary" className="mt-1 text-primary">{member.title}</Badge>
                                <div className="stats-tag mt-1" style={{ fontSize: '0.65rem' }}>{member.pos}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .text-navy { color: #002b5a; }
        .bg-navy { background-color: #002b5a; }

        /* Typography Sync */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1.5px; line-height: 1.1; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }

        .sync-dot-mini {
          width: 8px; height: 8px; background: #10b981;
          border-radius: 50%; display: inline-block;
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }

        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .icon-circle-mini {
          width: 42px; height: 42px; background: #f8fafc; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }

        .stats-tag { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; }

        .policy-list { list-style: none; padding: 0; }
        .policy-list li { position: relative; padding-left: 20px; color: #475569; line-height: 1.6; }
        .policy-list li::before { content: "→"; position: absolute; left: 0; color: #0055a4; font-weight: bold; }

        .step-num-circle {
            width: 32px; height: 32px; background: #0055a4; color: white;
            border-radius: 50%; display: flex; align-items: center; justify-content: center;
            font-weight: 800; font-size: 0.85rem; flex-shrink: 0;
        }

        .badge.bg-soft-primary { background: #e6f0ff; color: #0055a4; font-size: 0.65rem; }
        
        .hover-translate-y:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.05) !important; }
        .transition-all { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }

        @media (max-width: 991px) {
          .main-portal-container { margin-top: 0px; }
          .section-standard-spacing { padding-bottom: 30px; }
          .portal-heading { font-size: 1.6rem; word-break: break-word; letter-spacing: -1px; }
          .category-tag { font-size: 0.7rem; letter-spacing: 1.5px; }
          .details-card { padding: 1rem !important; }
        }

        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 85, 164, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          border: 2px solid #fff !important;
        }
      `}</style>
    </div>
  );
};

export default AdmissionPolicy;